import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search, ShieldCheck, Mail, Eye, MessageSquare, Bell, AlertTriangle } from "lucide-react";

const NT = "Not tracked";
const fmt = (v) => (v && v !== NT ? new Date(v).toLocaleString() : v || "—");

function Pill({ children, tone = "zinc" }) {
  const tones = {
    zinc: "bg-zinc-100 text-zinc-700", green: "bg-emerald-100 text-emerald-700",
    amber: "bg-amber-100 text-amber-700", rose: "bg-rose-100 text-rose-700",
    indigo: "bg-indigo-100 text-indigo-700",
  };
  return <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${tones[tone]}`}>{children}</span>;
}

const statusTone = (s) => ({ accepted: "green", declined: "rose", applied: "amber", shortlisted: "indigo", under_review: "indigo", waitlisted: "amber" }[s] || "zinc");

function EmailLogView({ log, testid }) {
  if (!log) return null;
  if (log.status !== "logged") {
    return (
      <div data-testid={testid} className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span><b>Email delivery: Unknown.</b> {log.note}</span>
      </div>
    );
  }
  return (
    <div data-testid={testid} className="mt-2 space-y-1">
      {log.logs.map((l, i) => (
        <div key={i} className="rounded-lg border border-zinc-100 bg-zinc-50 px-2.5 py-1.5 text-xs">
          <div className="flex flex-wrap items-center gap-2">
            <Mail className="h-3.5 w-3.5 text-zinc-400" />
            <span className="text-zinc-500">{fmt(l.created_at)}</span>
            <Pill tone={l.result === "accepted" ? "green" : l.result === "error" ? "rose" : "amber"}>{l.result}</Pill>
            {l.category && <span className="text-zinc-500">{l.category}</span>}
            <span className="text-zinc-400">delivery: {l.delivery_status}</span>
          </div>
          <div className="mt-0.5 text-zinc-600">{l.subject}{l.resend_id ? ` · resend_id ${l.resend_id}` : ""}{l.reason ? ` · ${l.reason}` : ""}{l.error ? ` · ${l.error}` : ""}</div>
        </div>
      ))}
    </div>
  );
}

function NotifList({ items, testid }) {
  if (!items?.length) return <p data-testid={`${testid}-empty`} className="text-xs text-zinc-400">None recorded</p>;
  return (
    <div data-testid={testid} className="space-y-1">
      {items.map((n, i) => (
        <div key={i} className="rounded-lg border border-zinc-100 px-2.5 py-1.5 text-xs">
          <div className="flex flex-wrap items-center gap-2">
            <Bell className="h-3.5 w-3.5 text-zinc-400" />
            <span className="font-mono text-zinc-500">{n.type}</span>
            <Pill tone={n.read ? "zinc" : "indigo"}>{n.read ? "read" : "unread"}</Pill>
            <span className="text-zinc-400">read at: {n.read_at}</span>
            <span className="ml-auto text-zinc-400">{fmt(n.created_at)}</span>
          </div>
          <div className="mt-0.5 text-zinc-700">{n.title} — {n.body}</div>
        </div>
      ))}
    </div>
  );
}

function MessageThread({ messages, toCreator, toBusiness, collabId, testid }) {
  return (
    <div data-testid={testid} className="mt-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400 flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" /> Conversation {collabId ? "" : "(no collaboration/conversation yet)"}</p>
      {messages?.length ? (
        <div className="mt-1 space-y-1">
          {messages.map((m, j) => (
            <div key={m.id || j} className="rounded-lg border border-zinc-100 px-2.5 py-1.5 text-xs">
              <div className="flex flex-wrap items-center gap-2">
                <Pill tone={m.sender_role === "business" ? "indigo" : "green"}>{m.sender_role}</Pill>
                <span className="text-zinc-400">{fmt(m.at)}</span>
                <span className="ml-auto text-zinc-400">read at: {m.read_at}</span>
              </div>
              <div className="mt-0.5 text-zinc-700">{m.text}</div>
            </div>
          ))}
        </div>
      ) : <p className="text-xs text-zinc-400">No messages recorded</p>}
      <div className="mt-2 grid gap-2 md:grid-cols-2">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">New-message alert → creator</p>
          <NotifList items={toCreator} testid={`${testid}-tocreator`} />
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">New-message alert → business</p>
          <NotifList items={toBusiness} testid={`${testid}-tobusiness`} />
        </div>
      </div>
    </div>
  );
}

function ConversationsBlock({ collaborations }) {
  if (!collaborations) return null;
  return (
    <div data-testid="investigate-conversations" className="rounded-2xl border border-zinc-200 bg-white p-4">
      <p className="mb-2 text-sm font-semibold text-zinc-800">All conversations ({collaborations.length})</p>
      {collaborations.length === 0 ? <p className="text-xs text-zinc-400">No conversations on record.</p> :
        <div className="space-y-3">
          {collaborations.map((c, i) => (
            <div key={c.collaboration_id || i} data-testid={`investigate-convo-${i}`} className="rounded-xl border border-zinc-100 bg-zinc-50/50 p-3">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-medium text-zinc-800">{c.campaign_title || "Conversation"}</span>
                <Pill tone={statusTone(c.status)}>{c.status}</Pill>
                <span className="text-zinc-500">with {c.with?.name} ({c.with?.role})</span>
                <span className="ml-auto text-xs text-zinc-400">{c.message_count} message(s)</span>
              </div>
              <MessageThread messages={c.collaboration_messages} toCreator={c.message_notifications_to_creator}
                toBusiness={c.message_notifications_to_business} collabId={c.collaboration_id} testid={`convo-${i}-thread`} />
            </div>
          ))}
        </div>}
    </div>
  );
}

function CreatorReport({ data }) {
  const ac = data.as_creator;
  return (
    <div className="space-y-4">
      <p className="text-sm text-zinc-500">{ac.applications_count} application(s) found · {ac.all_notifications.length} in-app notification(s) to this creator.</p>
      <ConversationsBlock collaborations={ac.collaborations} />
      <div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-zinc-400">Creator's email log</p>
        <EmailLogView log={ac.email_log} testid="creator-email-log" />
      </div>
      {ac.applications.map((a, i) => (
        <div key={a.application_id || i} data-testid={`investigate-app-${i}`} className="rounded-2xl border border-zinc-200 bg-white p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-display font-bold text-zinc-900">{a.opportunity?.title || "(opportunity unavailable / removed)"}</span>
            <Pill tone={statusTone(a.status)}>{a.status}</Pill>
            {a.match_band && a.match_band !== NT && <Pill tone="indigo">{a.match_band} match</Pill>}
          </div>
          <p className="mt-1 text-sm text-zinc-500">Business: <b>{a.business?.name || "—"}</b>{a.business?.email ? ` · ${a.business.email}` : ""}</p>
          <div className="mt-2 grid gap-2 text-sm sm:grid-cols-3">
            <div><span className="text-zinc-400">Submitted</span><br />{fmt(a.submitted_at)}</div>
            <div className="flex flex-col"><span className="text-zinc-400 inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> Business viewed</span>
              <span data-testid={`app-viewed-${i}`}>{a.viewed_by_business_at === NT ? <Pill tone="amber">Not tracked</Pill> : fmt(a.viewed_by_business_at)}</span></div>
            <div><span className="text-zinc-400">Response</span><br />{a.business_response.decided ? <Pill tone={statusTone(a.status)}>{a.status}</Pill> : <Pill tone="amber">awaiting response</Pill>}</div>
          </div>
          {a.message && <p className="mt-2 rounded-lg bg-zinc-50 p-2 text-sm text-zinc-600">“{a.message}”</p>}

          {a.status === "declined" && (a.business_response?.decline_reason_label || a.business_response?.decline_message) && (
            <div data-testid={`app-decline-feedback-${i}`} className="mt-2 rounded-lg border border-zinc-200 bg-white p-2.5 text-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Rejection feedback to creator</p>
              {a.business_response.decline_reason_label && <p className="mt-1 text-zinc-700"><b>Reason:</b> {a.business_response.decline_reason_label}</p>}
              {a.business_response.decline_message && <p className="mt-1 whitespace-pre-line text-zinc-700"><b>Message:</b> {a.business_response.decline_message}</p>}
              {a.business_response.decline_feedback_at && <p className="mt-1 text-xs text-zinc-400">Recorded {fmt(a.business_response.decline_feedback_at)}</p>}
            </div>
          )}

          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Business "new application" notification</p>
              <NotifList items={a.business_new_application_notifications} testid={`app-biznotif-${i}`} />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Creator decision notification</p>
              <NotifList items={a.creator_decision_notifications} testid={`app-crnotif-${i}`} />
            </div>
          </div>

          <div className="mt-3">
            <MessageThread messages={a.collaboration_messages} toCreator={a.message_notifications_to_creator}
              toBusiness={a.message_notifications_to_business} collabId={a.business_response.collaboration_id} testid={`app-thread-${i}`} />
          </div>

          <div className="mt-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Business email log</p>
            <EmailLogView log={a.business_email_log} testid={`app-bizemail-${i}`} />
          </div>
        </div>
      ))}
    </div>
  );
}

function BusinessReport({ data }) {
  const ab = data.as_business;
  return (
    <div className="space-y-4">
      <p className="text-sm text-zinc-500">{ab.opportunities_count} opportunit(y/ies).</p>
      <ConversationsBlock collaborations={ab.collaborations} />
      <div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-zinc-400">Business email log</p>
        <EmailLogView log={ab.email_log} testid="business-email-log" />
      </div>
      {ab.opportunities.map((o, i) => (
        <div key={o.opportunity?.id || i} data-testid={`investigate-opp-${i}`} className="rounded-2xl border border-zinc-200 bg-white p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-display font-bold text-zinc-900">{o.opportunity?.title}</span>
            <Pill tone={statusTone(o.opportunity?.status)}>{o.opportunity?.status}</Pill>
            <span className="text-sm text-zinc-500">{o.applicant_count} applicant(s)</span>
          </div>
          <div className="mt-2 space-y-2">
            {o.applicants.length === 0 ? <p className="text-sm text-zinc-400">No applicants</p> :
              o.applicants.map((a, j) => (
                <div key={a.application_id || j} data-testid={`investigate-opp-${i}-applicant-${j}`} className="rounded-xl border border-zinc-100 bg-zinc-50/50 p-3">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-medium text-zinc-800">{a.creator_name}</span>
                    <span className="text-zinc-400">{a.creator_email}</span>
                    <Pill tone={statusTone(a.status)}>{a.status}</Pill>
                    <span className="ml-auto text-xs text-zinc-500">submitted {fmt(a.submitted_at)}</span>
                  </div>
                  <div className="mt-1 text-xs text-zinc-500 inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> Business viewed: {a.viewed_by_business_at === NT ? <Pill tone="amber">Not tracked</Pill> : fmt(a.viewed_by_business_at)}</div>
                  {a.status === "declined" && (a.decline_reason_label || a.decline_message) && (
                    <div data-testid={`opp-${i}-applicant-${j}-decline-feedback`} className="mt-2 rounded-lg border border-zinc-200 bg-white p-2.5 text-xs">
                      <p className="font-semibold uppercase tracking-wide text-zinc-400">Rejection feedback</p>
                      {a.decline_reason_label && <p className="mt-1 text-zinc-700"><b>Reason:</b> {a.decline_reason_label}</p>}
                      {a.decline_message && <p className="mt-1 whitespace-pre-line text-zinc-700"><b>Message:</b> {a.decline_message}</p>}
                    </div>
                  )}
                  <MessageThread messages={a.collaboration_messages} toCreator={a.message_notifications_to_creator}
                    toBusiness={a.message_notifications_to_business} collabId={a.collaboration_id} testid={`opp-${i}-applicant-${j}-thread`} />
                </div>
              ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function AdminInvestigate({ initialQuery = "" }) {
  const [q, setQ] = useState(initialQuery);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (initialQuery && initialQuery.trim()) {
      setQ(initialQuery);
      (async () => {
        setBusy(true);
        try { const r = await api.get("/admin/investigate", { params: { q: initialQuery.trim() } }); setResult(r.data); }
        catch { setResult({ error: true }); }
        finally { setBusy(false); }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuery]);

  const run = async (opts = {}) => {
    const query = opts.user_id ? "" : q.trim();
    if (!opts.user_id && !query) return;
    setBusy(true);
    try {
      const r = await api.get("/admin/investigate", { params: opts.user_id ? { q, user_id: opts.user_id } : { q: query } });
      setResult(r.data);
    } catch {
      setResult({ error: true });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 space-y-5">
      <div className="flex items-start gap-2 rounded-2xl border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-800">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
        Read-only investigator. Search a creator or business by <b className="mx-1">@handle</b> or <b className="mx-1">email</b>. Nothing is changed — no status updates, resends, messages or read-marking. Timestamps show only where already recorded; otherwise “Not tracked”.
      </div>

      <div className="flex flex-wrap gap-2">
        <Input data-testid="investigate-input" value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && run()} placeholder="e.g. @crusetwins or someone@email.com" className="h-11 max-w-md" />
        <Button data-testid="investigate-search" onClick={() => run()} disabled={busy} className="h-11 rounded-full bg-zinc-900 hover:bg-zinc-800">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="mr-1.5 h-4 w-4" /> Investigate</>}
        </Button>
      </div>

      {result?.error && <p className="text-sm text-rose-600">Something went wrong running the search.</p>}

      {result && !result.error && !result.matched && !result.ambiguous && (
        <div data-testid="investigate-no-match" className="rounded-2xl border border-zinc-200 bg-white p-6 text-center text-zinc-500">
          {result.note || `No account found for “${result.query}”. Try the exact @handle or email as stored on the account.`}
        </div>
      )}

      {result?.ambiguous && (
        <div data-testid="investigate-candidates" className="rounded-2xl border border-zinc-200 bg-white p-4">
          <p className="mb-2 text-sm font-semibold text-zinc-800">Multiple accounts match{result.candidate_count ? ` (${result.candidate_count})` : ""} — pick one:</p>
          <div className="space-y-2">
            {result.candidates.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center gap-2 rounded-xl border border-zinc-100 p-3">
                <Pill tone={c.role === "creator" ? "green" : "indigo"}>{c.role}</Pill>
                <span className="font-medium text-zinc-900">{c.name}</span>
                <span className="text-sm text-zinc-500">{c.email}</span>
                <Button size="sm" data-testid={`investigate-pick-${c.id}`} onClick={() => run({ user_id: c.id })} className="ml-auto rounded-full">Select</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {result?.matched && (
        <div data-testid="investigate-result" className="space-y-4">
          <div className="rounded-2xl border border-zinc-200 bg-white p-4">
            <div className="flex flex-wrap items-center gap-2">
              <Pill tone={result.role === "creator" ? "green" : "indigo"}>{result.role}</Pill>
              <span className="font-display text-lg font-bold text-zinc-900">{result.account.display_name || result.account.business_name || result.account.name}</span>
              <Pill tone={result.account.status === "active" ? "green" : "rose"}>{result.account.status}</Pill>
            </div>
            <p className="mt-1 text-sm text-zinc-500">{result.account.email} · joined {fmt(result.account.created_at)}
              {result.account.handles && Object.values(result.account.handles).some(Boolean) ? ` · ${Object.entries(result.account.handles).filter(([, v]) => v).map(([k, v]) => `${k}: @${v}`).join(" · ")}` : ""}</p>
          </div>
          {result.role === "creator" ? <CreatorReport data={result} /> : result.role === "business" ? <BusinessReport data={result} /> :
            <p className="text-sm text-zinc-500">This account is an {result.role} — no application activity to show.</p>}
        </div>
      )}
    </div>
  );
}
